import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';

/**
 * Small stat card for the SolarStep1 (Kurz-Überblick) home page.
 * Displays a single metric with an icon, label, value and unit.
 *
 * API values often come as strings that already include the unit suffix
 * (e.g. "1596 W", "1,77 kWh", "Aktuell noch nötige Einnahmen: 3585,33 €").
 * The StatCard extracts the number AND unit for prominent display.
 *
 * Clicking the card opens a modal showing the raw API value (original text).
 * This is documented as a reusable pattern — see docs/ai-progress.md "StatCard-Muster".
 */
export const StatCard = ({ value, label, unit, icon, variant, subLabel, description }) => {
    const [showModal, setShowModal] = useState(false);

    // Parse API value: extract number AND optional embedded unit
    let displayValue = '–';
    let displayUnit = '';
    let rawValue = typeof value === 'string' ? value : String(value ?? '');

    if (typeof value === 'number' && !isNaN(value)) {
        displayValue = value.toLocaleString('de-DE', { maximumFractionDigits: 1 });
        displayUnit = unit || '';
    } else if (typeof value === 'string' && value.trim().length > 0) {
        // Try to find a number embedded in the string
        const numMatch = value.match(/([\d][\d,.]{2,})/);
        if (numMatch) {
            const raw = numMatch[1].replace(',', '.');
            const parsed = parseFloat(raw);
            if (!isNaN(parsed)) {
                displayValue = parsed.toLocaleString('de-DE', { maximumFractionDigits: 1 });
                // Extract trailing unit text after the number (e.g. "€", "kWh")
                const rest = value.trim().substring(numMatch.index + numMatch[0].length).trim();
                displayUnit = rest ? ' ' + rest : (unit || '');
            } else {
                displayValue = value.substring(0, 50);
                displayUnit = '';
            }
        } else {
            // Pure text (no extractable number) – keep as-is
            displayValue = value.substring(0, 40);
            displayUnit = unit || '';
        }
    }

    // Build description text: use provided description, or generate a default
    const descText = description || `„${label}“ — roher API-Wert.`;

    return (
        <>
            <div className={`stat-card ${variant || ''}`}
                 onClick={() => setShowModal(true)}
                 title="Klicken für Details">
                {icon && <span className="stat-card-icon">{icon}</span>}
                <div className="stat-card-content">
                    <div className="stat-card-value">
                        {displayValue}
                        {displayUnit ? <span className="stat-card-unit">{displayUnit}</span> : null}
                    </div>
                    <div className="stat-card-label">{label}</div>
                    {subLabel && <div className="stat-card-sub">{subLabel}</div>}
                </div>
            </div>

            {/* Detail Modal – shows the raw API value + description */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{label}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="detail-raw-value">{rawValue}</p>
                    {subLabel && <small>Details: {subLabel}</small>}
                    <div className="modal-description">
                        <i className="bi bi-info-circle me-1" />
                        <span>{descText}</span>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Schließen</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

/** Skeleton placeholder shown while API data is still loading */
export const StatCardSkeleton = ({ variant }) => (
    <div className={`stat-card stat-card-skeleton ${variant || ''}`}>
        <span className="stat-card-icon skeleton-pulse">
            <i className="bi bi-circle" />
        </span>
        <div className="stat-card-content">
            <div className="skeleton-line skeleton-lg" />
            <div className="skeleton-line skeleton-sm" />
        </div>
    </div>
);
